<script setup>
const route = useRoute()
const filters = defineModel('filters', { type: Array, default: () => [] })
const search = defineModel('search', { type: String, default: '' })
const disableTagAndGeoFilters = defineModel('disableTagAndGeoFilters', {
  type: Boolean,
  default: true,
})
const companyFilter = defineModel('companyFilter', {
  type: Boolean,
  default: false,
})
const companyQuery = defineModel('companyQuery', { type: String, default: '' })

const user = useStrapiUser()
const { getGroupedTags } = useApiTag()

const { data: tags } = await useCachedAsyncData(
  'groupedTags',
  () => getGroupedTags(),
  { default: () => [] },
)

const isProfessional = computed(() => user.value?.type === 'professional')
const isCompany = computed(() => user.value?.type === 'company')
const filterIds = computed(() => filters.value.map((f) => f.id))

function resetFilters() {
  filters.value = []
  search.value = ''
  disableTagAndGeoFilters.value = true
  companyQuery.value = ''
}

function toggleFilter(tag) {
  const foundTag = filters.value.find((t) => t.id === tag.id)
  if (foundTag) {
    filters.value = filters.value.filter((t) => t.id !== tag.id)
  } else {
    filters.value.push(tag)
  }
}

if (route?.query?.company) {
  companyQuery.value = route.query.company
}
</script>

<template>
  <div class="mb-4 w-full lg:mb-0 lg:w-1/3 lg:pr-14">
    <div class="mb-6 flex h-12 items-end justify-between px-4">
      <div class="text-lg text-gray-900">Filteropties</div>
      <div
        class="text-blue flex cursor-pointer items-center text-base font-medium"
        @click="resetFilters"
      >
        <IconRefresh class="mr-1 h-3" /> Reset
      </div>
    </div>
    <div class="w-full rounded-lg bg-white">
      <div class="p-3" :class="{ 'pb-0': tags.length }">
        <Input
          v-model="search"
          placeholder="Ik ben op zoek naar…"
          theme="pink"
          value=""
          class=""
        >
          <template #prefix>
            <IconMagnifier class="h-3 w-3 text-gray-600" />
          </template>
        </Input>

        <label v-if="isProfessional" class="mt-3 flex items-center gap-2">
          <input
            v-model="disableTagAndGeoFilters"
            type="checkbox"
            name="input"
            class="peer sr-only"
          />
          <div
            class="flex h-4 w-4 items-center justify-center rounded border-2 border-gray-200 bg-white peer-checked:border-none peer-checked:bg-primary"
          >
            <IconChecked class="w-2 text-white" />
          </div>
          <div class="mt-1 text-base text-gray-800">Toon alle vacatures</div>
          <InfoTippy
            content="Standaard wordt altijd een filter op basis van geselecteerde tags en op de locatie toegepast."
          />
        </label>
      </div>
      <div class="w-full">
        <div class="">
          <AccordionItem
            v-for="(parent, index) in tags"
            :key="index"
            class="border-b border-gray-200"
          >
            <template #title>
              <span class="text-lg capitalize text-gray-900">
                {{ parent.name }}
              </span>
            </template>

            <template v-if="parent.children.length" #content>
              <div
                v-for="(child, i) in parent.children"
                :key="i"
                class="flex items-center"
              >
                <label
                  class="flex items-center"
                  @click.prevent="toggleFilter(child)"
                >
                  <input
                    :checked="!!filterIds.includes(child.id)"
                    type="checkbox"
                    name="input"
                    class="peer sr-only"
                  />
                  <div
                    class="flex h-4 w-4 items-center justify-center rounded border-2 border-gray-200 bg-white peer-checked:border-none peer-checked:bg-primary"
                  >
                    <IconChecked class="w-2 text-white" />
                  </div>
                  <div class="ml-2 mt-1 text-base text-gray-800">
                    {{ child.name }}
                  </div>
                </label>
              </div>
            </template>
          </AccordionItem>

          <div v-if="isCompany" class="flex items-center p-6">
            <label class="flex items-center">
              <input
                v-model="companyFilter"
                type="checkbox"
                name="input"
                class="peer sr-only"
              />
              <div
                class="flex h-4 w-4 items-center justify-center rounded border-2 border-gray-200 bg-white peer-checked:border-none peer-checked:bg-primary"
              >
                <IconChecked class="w-2 text-white" />
              </div>
              <div class="ml-2 mt-1 text-base text-gray-800">
                Toon alleen eigen vacatures
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
